import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const YT_IFRAME_API_SRC = "https://www.youtube.com/iframe_api";

function loadYouTubeIframeAPI() {
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);

  return new Promise((resolve, reject) => {
    // If script already exists, just wait for the global callback.
    const existing = document.querySelector(`script[src="${YT_IFRAME_API_SRC}"]`);
    if (existing) {
      const prior = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (typeof prior === "function") prior();
        resolve(window.YT);
      };
      return;
    }

    const script = document.createElement("script");
    script.src = YT_IFRAME_API_SRC;
    script.async = true;
    script.onerror = () => reject(new Error("Failed to load YouTube IFrame API"));

    const prior = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prior === "function") prior();
      resolve(window.YT);
    };

    document.body.appendChild(script);
  });
}

export default function AdGate({ videoId, onComplete }) {
  const playerHostRef = useRef(null);
  const playerRef = useRef(null);
  const [status, setStatus] = useState("loading"); // loading | ready | ended | error
  const [countdown, setCountdown] = useState(null); // number | null
  const completedRef = useRef(false);

  const complete = useCallback(() => {
    if (completedRef.current) return;
    completedRef.current = true;
    onComplete?.();
  }, [onComplete]);

  const playerOptions = useMemo(
    () => ({
      height: "100%",
      width: "100%",
      videoId,
      playerVars: {
        autoplay: 1,
        mute: 1,
        controls: 1,
        rel: 0,
        modestbranding: 1,
        playsinline: 1,
        fs: 1,
      },
      events: {
        onReady: (event) => {
          // Autoplay is often blocked unless muted; start muted + explicitly play.
          try {
            event?.target?.mute?.();
            event?.target?.playVideo?.();
          } catch {
            // ignore
          }
          setStatus("ready");
        },
        onStateChange: (event) => {
          // 0 = ended
          if (event?.data === 0) {
            setStatus("ended");
          }
        },
        onError: () => setStatus("error"),
      },
    }),
    [videoId],
  );

  useEffect(() => {
    if (status !== "ended") return;
    setCountdown(3);

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev == null) return prev;
        if (prev <= 1) return 0;
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [status]);

  useEffect(() => {
    if (countdown === 0) complete();
  }, [countdown, complete]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const YT = await loadYouTubeIframeAPI();
        if (cancelled) return;
        if (!playerHostRef.current) return;

        playerRef.current = new YT.Player(playerHostRef.current, playerOptions);
      } catch (e) {
        if (!cancelled) setStatus("error");
      }
    })();

    return () => {
      cancelled = true;
      try {
        playerRef.current?.destroy?.();
      } catch {
        // ignore
      }
      playerRef.current = null;
    };
  }, [playerOptions]);

  return (
    <div className="adGate" aria-label="Advertisement">
      <div className="adGate__card">
        <div className="adGate__badge">Ad</div>
        <div className="adGate__title">Thanks for supporting Blåhaj</div>
        <div className="adGate__subtitle">
          The interview unlocks after the video finishes.
        </div>

        <div className="adGate__playerWrap">
          <div className="adGate__player" ref={playerHostRef} />
        </div>

        {status === "ended" ? (
          <div className="adGate__ctaRow">
            <button className="adGate__cta" type="button" onClick={complete}>
              Begin Interview
            </button>
            <div className="adGate__countdown" aria-live="polite">
              Starting in {countdown ?? 3}s…
            </div>
          </div>
        ) : null}

        {status === "error" ? (
          <div className="adGate__error">
            Couldn’t load the video. Try disabling strict tracking protections for this page.
            You can also open it directly:{" "}
            <a
              href={`https://www.youtube.com/shorts/${videoId}`}
              target="_blank"
              rel="noreferrer"
            >
              YouTube Short
            </a>
            .
          </div>
        ) : null}
      </div>
    </div>
  );
}


